import type { VideoTimelineState } from "./types";

/**
 * `useSyncedVideoTimeline`의 순수 타임라인 결정 로직. 수학을 rAF/effect 배선에서 떼어 두면
 * 까다로운 부분(길이 정규화(reconcile), virtual clock 진행, 채널 end stop)을 따로 단위
 * 테스트할 수 있다 — 훅은 이 함수들을 감싸는 얇은 배선이 된다.
 */

// boundTime()이 요청한 탐색(seek)을 다시 게시할 만큼 바꿨는지 판단하는 tolerance.
// (command/telemetry 리팩터 이후 현재 훅에서는 미사용 — 수학 테스트 용도로 보존.)
export const CONTROLLED_SEEK_REBASE_TOLERANCE_SECONDS = 0.02;

export const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

export function readFiniteDuration(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null;
}

/**
 * 표준 타임라인 길이: 외부 override가 우선, 없으면 알려진 채널 길이의 최댓값(아직 아무것도
 * 모르면 0).
 */
export function computeTimelineDuration(
  channelDurations: Iterable<number>,
  externalDuration: number | null | undefined,
): number {
  const external = readFiniteDuration(externalDuration);
  if (external !== null) return external;

  let maxDuration = 0;
  for (const duration of channelDurations) {
    maxDuration = Math.max(maxDuration, duration);
  }
  return maxDuration;
}

/** 탐색(seek) 목표를 타임라인 범위로 clamp. 유한수가 아니면 → 0; 길이가 없으면 → 0 이상으로만. */
export function boundToTimeline(seconds: number, duration: number): number {
  if (!Number.isFinite(seconds)) return 0;
  return duration > 0 ? clamp(seconds, 0, duration) : Math.max(0, seconds);
}

/**
 * 길이가 바뀔 때 타임라인 상태를 정규화(reconcile)한다. 적용할 patch를 반환하거나, 바꿀 게
 * 없으면 `null`. `duration <= 0`이면 깨끗한 정지 상태로 리셋해, 일시정지된 타임라인이 낡거나(stale)
 * 범위를 벗어난 값을 남기지 않게 한다.
 */
export function reconcileDurationPatch(
  state: VideoTimelineState,
  timelineDuration: number,
): Partial<VideoTimelineState> | null {
  if (timelineDuration <= 0) {
    if (state.durationSeconds !== 0 || state.currentTimeSeconds !== 0 || state.playing) {
      return { playing: false, currentTimeSeconds: 0, durationSeconds: 0 };
    }
    return null;
  }

  const patch: Partial<VideoTimelineState> = {};
  if (Math.abs(state.durationSeconds - timelineDuration) > 0.001) {
    patch.durationSeconds = timelineDuration;
  }
  // 줄어든 길이는 currentTime을 범위 밖으로 남긴다 — 그것도 clamp한다.
  if (state.currentTimeSeconds > timelineDuration) {
    patch.currentTimeSeconds = timelineDuration;
  }
  return Object.keys(patch).length > 0 ? patch : null;
}

/** wall-clock 경과로부터 virtual 재생 위치를 계산한다; 길이로 clamp하고 끝 도달 여부를 표시한다. */
export function advanceVirtualClock(params: {
  now: number;
  startedAt: number;
  startedTime: number;
  playbackRate: number;
  duration: number;
  endEpsilon: number;
}): { nextSeconds: number; reachedEnd: boolean } {
  const { now, startedAt, startedTime, playbackRate, duration, endEpsilon } = params;
  const elapsed = ((now - startedAt) / 1000) * Math.max(playbackRate, 0);
  const nextSeconds = clamp(startedTime + elapsed, 0, duration);
  const reachedEnd = nextSeconds >= Math.max(0, duration - endEpsilon);
  return { nextSeconds, reachedEnd };
}

/** 루프 (재)시작 위치: 이미 끝에 도달/초과했을 때만 0에서 다시 시작.
 *  (현재 훅에서는 미사용 — 수학 테스트 용도로 보존.) */
export function computeLoopStart(currentTime: number, duration: number): number {
  return duration > 0 && currentTime >= duration ? 0 : currentTime;
}

/** `play()` 시작 위치: 끝에서 `endEpsilon` 이내면 0에서 재시작, 아니면 범위로 bound. */
export function computePlayStart(
  currentTime: number,
  duration: number,
  endEpsilon: number,
): number {
  if (duration > 0 && currentTime >= Math.max(0, duration - endEpsilon)) return 0;
  return boundToTimeline(currentTime, duration);
}

/**
 * 채널별 목표 프레임. 타임라인보다 짧은 채널은 마지막 프레임을 지나 달리지 않고, 자기 끝
 * 근처에서 멈춰(그리고 blank) 선다.
 */
export function resolveChannelFrame(
  targetSeconds: number,
  channelDuration: number | undefined,
  endEpsilon: number,
): { hasEnded: boolean; seekTime: number } {
  if (channelDuration === undefined) {
    return { hasEnded: false, seekTime: targetSeconds };
  }
  const endStop = Math.max(0, channelDuration - endEpsilon);
  const hasEnded = targetSeconds >= endStop;
  return { hasEnded, seekTime: hasEnded ? endStop : targetSeconds };
}

/** 채널이 자기 길이를 지나 재생됐는지 여부(`blank` end 동작에서 사용). */
export function isChannelPastEnd(
  currentTime: number,
  channelDuration: number | undefined,
  endEpsilon: number,
): boolean {
  if (channelDuration === undefined) return false;
  return currentTime >= Math.max(0, channelDuration - endEpsilon);
}
